/**
 * Project capability catalogue — the honest vocabulary of what FixControl
 * can actually do for a project, mapped onto the functionality that really
 * exists in this codebase (bindings, provider health capabilities, sandbox
 * runtime, posture scanners, governance).
 *
 * A capability here is a *claim template*, not a grant: whether a project
 * actually HAS the capability is derived at runtime by the capability
 * resolver (src/server/projects/use-case-readiness.ts) from real bindings,
 * recorded integration_health, codebases, knowledge sources and governance —
 * never from a use-case selection.
 *
 * `healthCapabilities` names the integration_health rows
 * (provider/capability, see src/server/integrations/health/types.ts
 * PROVIDER_CAPABILITIES) that can evidence the capability. An empty list
 * means the capability is evidenced by in-product state (rows/config), not
 * by provider probes.
 */

export type ProjectCapabilityKind =
  /** Reading/ingesting into FixControl — no external side effects. */
  | "observe"
  /** Analysis inside FixControl (sandbox, scanners, grounded answering). */
  | "analyze"
  /** Preparing an external action (draft, patch, PR body) — still no send. */
  | "prepare"
  /** A real external write. Always governed: approval gate + outbox. */
  | "external_action";

/** The capability id union — single source for typed references from
 *  use-case manifests. */
export const PROJECT_CAPABILITY_IDS = [
  "intake.receive",
  "knowledge.answer",
  "tickets.read",
  "reply.govern",
  "reply.send",
  "code.read",
  "sandbox.analyze",
  "delivery.pr",
  "deployment.observe",
  "deployment.promote",
  "security.scan",
  "approvals.route",
  "notifications.post",
] as const;
export type ProjectCapabilityId = (typeof PROJECT_CAPABILITY_IDS)[number];

export type ProjectCapabilityDefinition = {
  id: ProjectCapabilityId;
  kind: ProjectCapabilityKind;
  labelKey: string;
  descriptionKey: string;
  /** integration_health evidence sources, as "provider/capability". */
  healthCapabilities: string[];
  /** External actions always require a human decision (gate or comm
   *  approval) before anything leaves FixControl. */
  requiresApproval: boolean;
};

const cap = (
  id: ProjectCapabilityId,
  kind: ProjectCapabilityKind,
  healthCapabilities: string[],
): ProjectCapabilityDefinition => ({
  id,
  kind,
  labelKey: `useCases.capabilities.${id.replace(/\./g, "_")}.label`,
  descriptionKey: `useCases.capabilities.${id.replace(/\./g, "_")}.description`,
  healthCapabilities,
  requiresApproval: kind === "external_action",
});

/** The catalogue. Each entry is backed by shipped functionality:
 *  - intake.receive      — intake bindings (purpose `intake`), mailboxes,
 *                          website-chat widgets (src/server/intake-agent/).
 *  - knowledge.answer    — grounded KB answering over linked knowledge
 *                          sources (src/server/intake-agent/support/answer.ts).
 *  - tickets.read        — read-only ticket-question answering over
 *                          FixControl + bound Jira/Freshdesk
 *                          (src/server/tickets/ticket-question-answering.ts).
 *  - reply.govern        — governed reply pipeline: safety scan + comms
 *                          policy + outbound_email_drafts (src/lib/comm-governance/).
 *  - reply.send          — external reply delivery via outbox (Gmail/O365
 *                          send_reply, forward mailboxes via Resend,
 *                          Freshdesk post_public_reply, JSM customer reply).
 *  - code.read           — repository read access (git-host read_repo/read_file).
 *  - sandbox.analyze     — sandbox runtime analysis/tests
 *                          (src/server/sandbox-runtime/).
 *  - delivery.pr         — PR/MR preparation + delivery via outbox
 *                          (create_branch/create_pr/write_patch; PR-only).
 *  - deployment.observe  — deployment observation: devops observe-and-govern
 *                          ingest + Argo read_rollout/read_status.
 *  - deployment.promote  — the ONE governed deployment action that exists:
 *                          Argo Rollouts promote via deployment gate +
 *                          argo.promote outbox. No other provider has
 *                          execution — observation never implies action.
 *  - security.scan       — posture scanners that actually ship: gitleaks,
 *                          npm-audit, semgrep, osv, trivy
 *                          (src/server/codebase-inspection/posture/).
 *  - approvals.route     — approval routing + decision ledger (in-product,
 *                          decideOrchestrationApproval chokepoint).
 *  - notifications.post  — status/notification posting to bound Slack/Teams
 *                          channels via outbox. */
export const PROJECT_CAPABILITIES: readonly ProjectCapabilityDefinition[] = [
  cap("intake.receive", "observe", []),
  cap("knowledge.answer", "analyze", []),
  cap("tickets.read", "observe", ["jira/read_issue", "freshdesk/read_ticket"]),
  cap("reply.govern", "prepare", []),
  cap("reply.send", "external_action", [
    "gmail/send_reply",
    "office365/send_reply",
    // Forward mailboxes reply through Resend (forward-reply.ts).
    "resend/send_reply",
    "freshdesk/post_public_reply",
    "jira/jsm_customer_reply",
  ]),
  cap("code.read", "observe", ["github/read_repo", "gitlab/read_repo"]),
  cap("sandbox.analyze", "analyze", []),
  cap("delivery.pr", "external_action", ["github/create_pr", "gitlab/create_pr"]),
  cap("deployment.observe", "observe", ["argo/read_rollout", "argo/read_status"]),
  cap("deployment.promote", "external_action", ["argo/promote_rollout"]),
  cap("security.scan", "analyze", []),
  cap("approvals.route", "prepare", []),
  cap("notifications.post", "external_action", [
    "slack/post_message",
    "teams/post_message",
  ]),
] as const;

export function isProjectCapabilityId(v: unknown): v is ProjectCapabilityId {
  return (
    typeof v === "string" &&
    (PROJECT_CAPABILITY_IDS as readonly string[]).includes(v)
  );
}

export function projectCapabilityById(
  id: string,
): ProjectCapabilityDefinition | undefined {
  return PROJECT_CAPABILITIES.find((c) => c.id === id);
}
